import App from "../../app";
import { AttemptId, ItemId, ProductId, QuizId } from "../../model/datasource";
import { CardHeaderView, CardItemView } from "../impl/card";
import { HeaderView, NullHeaderView } from "./header";
import { ItemView, NullItemView } from "./item";
import { ListView, NullListView } from "./list";
import { NullScoreView, ScoreView } from "./score";
import { NullSectionView, SectionView } from "./section";
import { NullSettingsView, SettingsView } from "./settings";
import { NullSummaryView, SummaryView } from "./summary";
import { ChartView, NullChartView } from "./chart";

const currentScript = document.currentScript;

export interface ViewProps<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> {
	item?: ItemView<P, A, Z, I>;
	list?: ListView<P, A, Z, I>;
	header?: HeaderView<P, A, Z, I>;
	scores?: ScoreView<P, A, Z, I>;
	settings?: SettingsView<P, A, Z, I>;
	sections?: SectionView<P, A, Z, I>;
	summary?: SummaryView<P, A, Z, I>;
	chart?: ChartView<P, A, Z, I>;
}

export default class View<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> {
	item: ItemView<P, A, Z, I>;
	list: ListView<P, A, Z, I>;
	header: HeaderView<P, A, Z, I>;
	scores: ScoreView<P, A, Z, I>;
	settings: SettingsView<P, A, Z, I>;
	sections: SectionView<P, A, Z, I>;
	summary: SummaryView<P, A, Z, I>;
	chart: ChartView<P, A, Z, I>;
	bindTarget: Node;

	constructor({item, list, header, scores, settings, sections, summary, chart}: ViewProps<P, A, Z, I>) {
		this.item = item ?? new NullItemView();
		this.list = list ?? new NullListView();
		this.header = header ?? new NullHeaderView();
		this.scores = scores ?? new NullScoreView();
		this.settings = settings ?? new NullSettingsView();
		this.sections = sections ?? new NullSectionView();
		this.summary = summary ?? new NullSummaryView();
		this.chart = chart ?? new NullChartView();

		this.bindTarget = document.createElement('div');
		document.querySelector('body').append(this.bindTarget);
	}

	init(app: App<P, A, Z, I>): void {
		this.item.init(app);
		this.list.init(app);
		this.header.init(app);
		this.scores.init(app);
		this.settings.init(app);
		this.sections.init(app);
		this.summary.init(app);
		this.chart.init(app);
	}

	getBindTarget(): EventTarget { return this.bindTarget; }

	loadScript({ src, type, timeout = 5000 }: {
		src: string,
		type?: string,
		timeout?: number
	}): Promise<HTMLScriptElement> {
		return new Promise((resolve, reject) => {
			const script: HTMLScriptElement = document.createElement('script');
			script.src = src;
			script.type = type ?? 'text/javascript';
			currentScript.parentNode.insertBefore(script, currentScript);
			const resolver = () => { resolve(script); };
			const rejector = (err) => { reject(err); };
			script.addEventListener('load', resolver);
			script.addEventListener('error', rejector);
			setTimeout(() => {
				script.removeEventListener('load', resolver);
				script.removeEventListener('error', rejector);
				reject(new Error(JSON.stringify({ message: 'Script timeout', src, type, timeout })));
			}, timeout);
		});
	}
}

export class PqView<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> extends View<P, A, Z, I> { }

export class FlashView<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> extends View<P, A, Z, I> {
	item: CardItemView<P, A, Z, I>;
	header: CardHeaderView<P, A, Z, I>;
}
